/**
 * Dot-paths through `Auth` whose leaf is a `string | number` — the only shapes
 * usable as a stable identity key. A game that types its auth gets a compile
 * error on a typo or a renamed field; an untyped game (`Auth` = any) widens to
 * plain `string`, so nothing pre-existing breaks.
 *
 * Depth is bounded (4 levels) by unrolling 4 distinct, non-self-referential
 * type levels (`Depth0`..`Depth3`) rather than a recursive type parameterized
 * by a depth counter. A self-recursive conditional type whose result is
 * embedded inside a template literal (`` `${K}.${Recurse<...>}` ``) is not in
 * "tail position", so TypeScript's tail-recursion optimization for recursive
 * conditional types does not apply to it — and without that optimization,
 * TypeScript hits its instantiation-depth limit (TS2589 "Type instantiation
 * is excessively deep and possibly infinite") almost immediately, even for a
 * flat, unnested object and even under a non-strict `tsconfig`. Unrolling
 * sidesteps the limitation entirely: each level is its own ordinary generic
 * type, so there is no recursion for the compiler to bound.
 *
 * `NonNullable<T[K]>` unwraps an optional property (`T[K]` including
 * `undefined`) before testing it against `Scalar`/`object`, so an optional
 * scalar field (common on JWT/claims-shaped auth) is still a valid path.
 */
type Scalar = string | number;

type ScalarKeyOf<T> = keyof {
    [K in keyof T & string as NonNullable<T[K]> extends Scalar ? K : never]: 1
};

type ObjectKeyOf<T> = keyof {
    [K in keyof T & string as
        NonNullable<T[K]> extends Scalar ? never :
        NonNullable<T[K]> extends object ? K :
        never
    ]: 1
};

type Depth0<T> = ScalarKeyOf<T>;
type Depth1<T> = ScalarKeyOf<T> | { [K in ObjectKeyOf<T>]: K extends keyof T ? `${K}.${Depth0<NonNullable<T[K]>>}` : never }[ObjectKeyOf<T>];
type Depth2<T> = ScalarKeyOf<T> | { [K in ObjectKeyOf<T>]: K extends keyof T ? `${K}.${Depth1<NonNullable<T[K]>>}` : never }[ObjectKeyOf<T>];
type Depth3<T> = ScalarKeyOf<T> | { [K in ObjectKeyOf<T>]: K extends keyof T ? `${K}.${Depth2<NonNullable<T[K]>>}` : never }[ObjectKeyOf<T>];

type PathsOf<T> = Depth3<T>;

export type AuthIdentityPath<Auth> =
    // `any`/`unknown` auth carries no information to check against — accept any string.
    unknown extends Auth ? string :
    PathsOf<Auth> extends never ? string : PathsOf<Auth>;

/**
 * Read the identity value out of a server-verified auth object.
 *
 * Returns `undefined` — never throws, never guesses — when the path is unset,
 * the auth is nullish, a segment is missing, or the leaf is not a non-empty
 * scalar. Callers MUST treat `undefined` as "this client has no durable
 * identity": no seat held, normal join. Falling back to `privateId` would
 * reintroduce the per-session matching this whole change exists to remove.
 */
export function resolveAuthIdentity(auth: any, path: string | undefined): string | undefined {
    if (!path || auth === null || auth === undefined) return undefined;
    let cur: any = auth;
    for (const segment of path.split('.')) {
        if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
        cur = cur[segment];
    }
    if (typeof cur === 'number' && Number.isFinite(cur)) return String(cur);
    if (typeof cur === 'string' && cur !== '') return cur;
    return undefined;
}
